export const resolvePublicBaseUrl = (): string => {
  const raw = import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined;

  if (raw) {
    try {
      const parsed = new URL(raw);
      const path = parsed.pathname.endsWith("/") ? parsed.pathname.slice(0, -1) : parsed.pathname;
      return `${parsed.origin}${path}`;
    } catch (error) {
      console.warn("Invalid VITE_PUBLIC_SITE_URL value", error);
    }
  }

  if (typeof window !== "undefined" && window.location) {
    return window.location.origin;
  }

  return "";
};

export const supportsNativeShare = () => typeof navigator !== "undefined" && typeof navigator.share === "function";

export const copyTextToClipboard = async (text: string) => {
  if (typeof navigator === "undefined" || !navigator.clipboard) {
    throw new Error("Clipboard API is not available in this environment");
  }

  await navigator.clipboard.writeText(text);
};

export const openMailClient = (mailtoHref: string) => {
  if (typeof window === "undefined") return;

  try {
    if (typeof document !== "undefined" && document.body) {
      const anchor = document.createElement("a");
      anchor.href = mailtoHref;
      anchor.target = "_blank";
      anchor.rel = "noreferrer noopener";
      anchor.style.position = "absolute";
      anchor.style.width = "0";
      anchor.style.height = "0";
      anchor.style.overflow = "hidden";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return;
    }
  } catch (error) {
    console.warn("Falling back to window navigation for mailto link", error);
  }

  const newWindow = window.open(mailtoHref, "_blank");
  if (!newWindow) {
    window.location.href = mailtoHref;
  }
};

interface ShareMessageOptions {
  movieName: string;
  formName: string;
  password?: string | null;
}

export const buildShareMessage = ({ movieName, formName, password }: ShareMessageOptions) => {
  return password
    ? `Please review "${movieName}" using the ${formName} form. Password: ${password}`
    : `Please review "${movieName}" using the ${formName} form.`;
};

interface EmailLinkOptions extends ShareMessageOptions {
  url: string;
  recipients?: string[];
}

export const buildEmailHref = ({ movieName, formName, url, password, recipients = [] }: EmailLinkOptions) => {
  const subject = `Feedback request: ${movieName} – ${formName}`;
  const passwordLine = password ? `\nPassword: ${password}` : "";
  const body = `Hi,\n\nPlease share your feedback on "${movieName}" using the ${formName} form:\n${url}${passwordLine}\n\nThank you!`;

  const toList = recipients
    .map((recipient) => recipient.trim())
    .filter((recipient) => recipient.length > 0);

  const mailtoBase = toList.length > 0 ? `mailto:${toList.join(",")}` : "mailto:";

  return `${mailtoBase}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

interface NativeShareOptions extends ShareMessageOptions {
  url: string;
}

export const shareViaNavigator = async ({ movieName, formName, url, password }: NativeShareOptions) => {
  if (!supportsNativeShare()) {
    throw new Error("Native share is not supported");
  }

  const title = `${movieName} – ${formName}`;
  const text = buildShareMessage({ movieName, formName, password });

  await navigator.share({ title, text, url });
};

interface AnalysisShareOptions {
  movieName: string;
  formName: string;
  url: string;
}

export const buildAnalysisEmailHref = ({ movieName, formName, url }: AnalysisShareOptions) => {
  const subject = `Analysis ready: ${movieName} – ${formName}`;
  const body = `Hi,\n\nHere are the latest analysis results for "${movieName}" (${formName}):\n${url}\n\nThanks!`;

  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export const shareAnalysisViaNavigator = async ({ movieName, formName, url }: AnalysisShareOptions) => {
  if (!supportsNativeShare()) {
    throw new Error("Native share is not supported");
  }

  const title = `${movieName} – ${formName} analysis`;
  const text = `View the latest analysis for "${movieName}" using the ${formName} results.`;

  await navigator.share({ title, text, url });
};
